import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Select from 'react-select';
import clsx from 'clsx';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import NumberInput from '@/components/inputControls/NumberInput';
import { PageNavbar } from '@/pages/account';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { UserRoles } from '@/constant';
import { useCurrentUserRoles } from '@/utils/userRole';
import {
  useGetCoastalAreaQuery,
  useBulkImportHarborsMutation,
} from '@/services/HarborManagementService';
import { useManagedListOptions } from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';
import {
  BulkImportFieldError,
  BulkImportRow,
  BULK_IMPORT_MAX_ROWS,
} from './harborBulkImport.types';
import {
  createEmptyBulkImportRow,
  getRowFieldErrors,
  parseJsonInput,
  readJsonFile,
  rowsToApiPayload,
  validateBulkImportRows,
} from './harborBulkImport.utils';
import { HarborImportMapPreview } from './HarborImportMapPreview';
import type { BulkImportCreatedHarbor } from '@/types/harbor-management.types';
import { BackButton } from '@/components/button';

const TEMPLATE_URL = '/templates/harbour-import-template.json';

export function HarborBulkImport() {

  const { enqueueSnackbar } = useSnackbar();
  const { currentLayout } = useLayout();
  const roles = useCurrentUserRoles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPlatformAdmin = roles.includes(UserRoles.ADMIN);

  const [jsonText, setJsonText] = useState('');
  const [rows, setRows] = useState<BulkImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    BulkImportFieldError[]
  >([]);
  const [createdHarbors, setCreatedHarbors] = useState<
    BulkImportCreatedHarbor[]
  >([]);

  const { keys: categoryKeys, options: categoryOptions } =
    useManagedListOptions(ManagedListType.HARBOR_CATEGORY);
  const { data: coastalAreasData } = useGetCoastalAreaQuery({});
  const [bulkImport, { isLoading: isImporting }] =
    useBulkImportHarborsMutation();

  const categoryKeySet = useMemo(() => new Set(categoryKeys), [categoryKeys]);
  const coastalAreaSet = useMemo(
    () => new Set((coastalAreasData ?? []).map((a) => a.name)),
    [coastalAreasData]
  );

  const coastalOptions = useMemo(
    () =>
      (coastalAreasData ?? []).map((a) => ({
        value: a.name,
        label: a.label || a.name,
      })),
    [coastalAreasData]
  );

  const resetBulkImportPage = useCallback(() => {
    setJsonText('');
    setRows([]);
    setParseError(null);
    setValidationErrors([]);
    setCreatedHarbors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  if (!isPlatformAdmin) {
    return <Navigate to="/error/404" replace />;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const loadFromJson = useCallback((text: string) => {
    const result = parseJsonInput(text);
    if (result.error) {
      setParseError(result.error);
      return;
    }
    setParseError(null);
    setValidationErrors([]);
    setCreatedHarbors([]);
    setRows(result.rows ?? []);
  }, []);

  const handleLoadJson = () => {
    loadFromJson(jsonText);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const result = await readJsonFile(file);
    if (result.error) {
      setParseError(result.error);
      enqueueSnackbar(result.error, { variant: 'error' });
      return;
    }
    setParseError(null);
    setValidationErrors([]);
    setCreatedHarbors([]);
    setRows(result.rows ?? []);
    try {
      const text = await file.text();
      setJsonText(text);
    } catch {
      // grid already loaded
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleValidate = () => {
    const errors = validateBulkImportRows(rows, categoryKeySet, coastalAreaSet);
    setValidationErrors(errors);
    if (errors.length === 0) {
      enqueueSnackbar('All rows passed validation.', { variant: 'success' });
    } else {
      enqueueSnackbar('Fix validation errors before importing.', {
        variant: 'error',
      });
    }
  };

  const handleImport = async () => {
    const errors = validateBulkImportRows(rows, categoryKeySet, coastalAreaSet);
    setValidationErrors(errors);
    if (errors.length > 0) {
      enqueueSnackbar('Fix validation errors before importing.', {
        variant: 'error',
      });
      return;
    }

    try {
      const result = await bulkImport({
        rows: rowsToApiPayload(rows),
      }).unwrap();
      resetBulkImportPage();
      setCreatedHarbors(result.created || []);
      enqueueSnackbar(
        `Successfully imported ${result.created.length} harbour(s).`,
        { variant: 'success' }
      );
    } catch (err: unknown) {
      const apiErr = err as {
        data?: { errors?: BulkImportFieldError[]; message?: string };
      };
      if (apiErr?.data?.errors?.length) {
        setValidationErrors(apiErr.data.errors);
      }
      enqueueSnackbar('Import failed. Check errors below.', {
        variant: 'error',
      });
    }
  };

  const updateRow = (id: string, field: keyof BulkImportRow, value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
    setValidationErrors([]);
    setCreatedHarbors([]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setValidationErrors([]);
  };

  const addRow = () => {
    if (rows.length >= BULK_IMPORT_MAX_ROWS) {
      return;
    }
    setRows((prev) => [...prev, createEmptyBulkImportRow()]);
    setValidationErrors([]);
  };

  const globalErrors = validationErrors.filter((e) => e.row === 0);
  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;

  return (
    <Fragment>
      <PageNavbar />
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <div className="flex items-center gap-3">
                <BackButton customBackPath="/habor-management" />
                <div>
                  <ToolbarPageTitle text="Harbour bulk import" />
                  <ToolbarDescription>
                    Paste or upload a JSON array (1–30 harbours), review rows,
                    then import as draft records.
                  </ToolbarDescription>
                </div>
              </div>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="card mb-5">
          <div className="card-body space-y-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="bulk-import-json"
                >
                  JSON array
                </label>
                <p className="text-2sm text-gray-500 mb-0">
                  Paste or upload a JSON array (1–30 harbours), review rows,
                  then import as draft records.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <a
                  href={TEMPLATE_URL}
                  download="harbour-import-template.json"
                  className="btn btn-sm btn-light"
                >
                  Download JSON template
                </a>
                <label className="btn btn-sm btn-light cursor-pointer mb-0">
                  Upload JSON file
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleLoadJson}
                  disabled={!jsonText.trim()}
                >
                  Load into table
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <textarea
                id="bulk-import-json"
                className={clsx(
                  'textarea textarea-bordered w-full min-h-[220px] resize-y rounded-lg font-mono text-sm leading-6',
                  parseError && 'border-red-500 focus:border-red-500'
                )}
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder='[{"name":"...","latitude":59.1,"longitude":20.5,...}]'
                spellCheck={false}
              />
              {parseError && (
                <p className="text-red-500 text-xs mt-1" role="alert">
                  {parseError}
                </p>
              )}
            </div>

            {globalErrors.length > 0 && (
              <ul className="text-danger text-sm list-disc ps-5" role="alert">
                {globalErrors.map((e, i) => (
                  <li key={`${e.field}-${i}`}>{e.message}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="card mb-5">
          <div className="card-header flex justify-between items-center">
            <h3 className="card-title mb-0">
              Review &amp; edit rows ({rows.length}/{BULK_IMPORT_MAX_ROWS})
            </h3>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={addRow}
                disabled={rows.length >= BULK_IMPORT_MAX_ROWS}
              >
                Add row
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleValidate}
                disabled={rows.length === 0}
              >
                Validate
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleImport}
                disabled={rows.length === 0 || isImporting}
              >
                {isImporting ? 'Importing…' : 'Import harbours'}
              </button>
            </div>
          </div>

          <div className="card-body overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-gray-500 text-sm">
                Load JSON or add rows to begin.
              </p>
            ) : (
              <table className="table table-sm table-bordered align-middle text-sm min-w-[1320px]">
                <thead>
                  <tr>
                    <th className="w-[52px] text-center">#</th>
                    <th className="min-w-[220px]">Name</th>
                    <th className="min-w-[170px]">Location</th>
                    <th className="min-w-[120px]">Latitude</th>
                    <th className="min-w-[120px]">Longitude</th>
                    <th className="min-w-[210px]">Category</th>
                    <th className="min-w-[230px]">Coastal area</th>
                    <th className="min-w-[100px]">Country</th>
                    <th className="min-w-[120px]">Min depth (m)</th>
                    <th className="min-w-[120px]">Max depth (m)</th>
                    <th className="min-w-[120px]">Berths</th>
                    <th className="w-[64px]" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const fieldErrors = getRowFieldErrors(
                      validationErrors,
                      index
                    );
                    const inputClass = (field: string) =>
                      fieldErrors[field]
                        ? 'input input-bordered w-full border-red-500 focus:border-red-500'
                        : 'input input-bordered w-full';
                    const textareaClass = (field: string) =>
                      fieldErrors[field]
                        ? 'textarea textarea-bordered w-full border-red-500 focus:border-red-500'
                        : 'textarea textarea-bordered w-full';
                    return (
                      <tr key={row.id}>
                        <td className="text-center text-gray-600">
                          {index + 1}
                        </td>
                        <td>
                          <input
                            className={inputClass('name')}
                            value={row.name}
                            onChange={(e) =>
                              updateRow(row.id, 'name', e.target.value)
                            }
                            aria-invalid={!!fieldErrors.name}
                          />
                          {fieldErrors.name && (
                            <span className="text-danger text-xs">
                              {fieldErrors.name}
                            </span>
                          )}
                          <textarea
                            className={`${textareaClass('description')} mt-2 text-sm`}
                            rows={2}
                            value={row.description}
                            onChange={(e) =>
                              updateRow(row.id, 'description', e.target.value)
                            }
                            placeholder="Description"
                          />
                        </td>
                        <td>
                          <input
                            className={inputClass('location')}
                            value={row.location}
                            onChange={(e) =>
                              updateRow(row.id, 'location', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            name="latitude"
                            inputClassName={inputClass('latitude')}
                            value={row.latitude}
                            min={-90}
                            max={90}
                            step="any"
                            maxDecimals={100}
                            error={fieldErrors.latitude}
                            onChange={(e) =>
                              updateRow(row.id, 'latitude', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            name="longitude"
                            inputClassName={inputClass('longitude')}
                            value={row.longitude}
                            min={-180}
                            max={180}
                            step="any"
                            maxDecimals={100}
                            error={fieldErrors.longitude}
                            onChange={(e) =>
                              updateRow(row.id, 'longitude', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <Select
                            inputId={`bulk-import-category-${row.id}`}
                            options={categoryOptions}
                            value={
                              categoryOptions.find(
                                (option) => option.value === row.category
                              ) ?? null
                            }
                            onChange={(option) =>
                              updateRow(row.id, 'category', option?.value ?? '')
                            }
                            isClearable
                            placeholder="Category"
                            className={clsx(
                              'react-select-container react-select-container--small',
                              fieldErrors.category &&
                                'react-select-container--error'
                            )}
                            classNamePrefix="react-select"
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                          />
                          {fieldErrors.category && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {fieldErrors.category}
                            </span>
                          )}
                        </td>
                        <td>
                          <Select
                            inputId={`bulk-import-coastal-${row.id}`}
                            options={coastalOptions}
                            value={
                              coastalOptions.find(
                                (option) => option.value === row.coastal_area
                              ) ?? null
                            }
                            onChange={(option) =>
                              updateRow(
                                row.id,
                                'coastal_area',
                                option?.value ?? ''
                              )
                            }
                            isClearable
                            placeholder="Coastal area"
                            className={clsx(
                              'react-select-container react-select-container--small',
                              fieldErrors.coastal_area &&
                                'react-select-container--error'
                            )}
                            classNamePrefix="react-select"
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                          />
                          {fieldErrors.coastal_area && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {fieldErrors.coastal_area}
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            className={inputClass('country_short_code')}
                            value={row.country_short_code}
                            maxLength={2}
                            onChange={(e) =>
                              updateRow(
                                row.id,
                                'country_short_code',
                                e.target.value.toUpperCase()
                              )
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            name="min_depth"
                            inputClassName={inputClass('min_depth')}
                            value={row.min_depth}
                            step="0.01"
                            maxDecimals={2}
                            error={fieldErrors.min_depth}
                            onChange={(e) =>
                              updateRow(row.id, 'min_depth', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            name="max_depth"
                            inputClassName={inputClass('max_depth')}
                            value={row.max_depth}
                            step="0.01"
                            maxDecimals={2}
                            error={fieldErrors.max_depth}
                            onChange={(e) =>
                              updateRow(row.id, 'max_depth', e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            name="berth_capacity"
                            inputClassName={inputClass('berth_capacity')}
                            value={row.berth_capacity}
                            min={0}
                            step={1}
                            maxDecimals={0}
                            error={fieldErrors.berth_capacity}
                            onChange={(e) =>
                              updateRow(
                                row.id,
                                'berth_capacity',
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-icon btn-sm btn-light text-danger"
                            onClick={() => removeRow(row.id)}
                            aria-label="Remove row"
                          >
                            <KeenIcon icon="trash" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {rows.length > 0 && (
          <div className="card mb-5">
            <div className="card-header">
              <h3 className="card-title mb-0">Coordinate preview</h3>
            </div>
            <div className="card-body p-0">
              <HarborImportMapPreview rows={rows} />
            </div>
          </div>
        )}

        {createdHarbors.length > 0 && (
          <div className="card mb-5">
            <div className="card-header">
              <h3 className="card-title mb-0">Imported harbours</h3>
            </div>
            <div className="card-body">
              <ul className="list-unstyled mb-0">
                {createdHarbors.map((h) => (
                  <li key={h.id} className="mb-2">
                    <Link to={`/harbors/edit/${h.id}`} className="text-primary">
                      {h.name}
                    </Link>
                    {h.location ? (
                      <span className="text-gray-500 ms-2">— {h.location}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-3 mb-0">
                Imported harbours are saved as drafts and are not published on
                the public map until you publish them in harbour management.
              </p>
            </div>
          </div>
        )}
      </Container>
    </Fragment>
  );
}
